"use client";

import Link from "next/link";
import {
  forwardRef,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type ComponentType,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  CircleAlert,
  CircleCheck,
  Eye,
  EyeOff,
  LoaderCircle,
  ShieldCheck,
} from "lucide-react";
import { AuthShowcase, type ShowcaseMode } from "./AuthShowcase";

/* --------------------------------------------------------------------------
 * Page frame, in the same look as the admin sign-in: a dark night canvas with
 * two slow glows and a faint grid, the story on the left (desktop) and a white
 * card with a gradient edge on the right. On a phone the card sits under a
 * slim logo bar and the whole page fits one screen; it only scrolls as a
 * fallback (very small screens, long error messages).
 * ------------------------------------------------------------------------ */

export function AuthShell({
  mode,
  children,
  footer,
}: {
  mode: ShowcaseMode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="rb-auth relative h-dvh overflow-y-auto overflow-x-hidden bg-[#140f33] text-white">
      <style>{AUTH_CSS}</style>
      {/* backdrop: two slow glows and a faint grid */}
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 overflow-hidden"
      >
        <div className="rbl-glow absolute -left-[280px] -top-[280px] h-[800px] w-[800px] rounded-full bg-[radial-gradient(closest-side,#6a52ff,rgba(106,82,255,0.45)_40%,transparent)] opacity-40 will-change-transform" />
        <div className="rbl-glow absolute -bottom-[340px] -right-[270px] h-[800px] w-[800px] rounded-full bg-[radial-gradient(closest-side,#ff6fb5,rgba(255,111,181,0.45)_40%,transparent)] opacity-20 will-change-transform [animation-delay:-6s]" />
        <div className="rbl-grid absolute inset-0" />
      </div>

      <div className="relative mx-auto flex min-h-dvh w-full max-w-[1240px] flex-col px-4 pb-8 pt-[max(1rem,env(safe-area-inset-top))] sm:px-8 lg:px-12 lg:pt-8">
        {/* one row: a way back home on the left, the colour logo on the right */}
        <header className="flex items-center justify-between">
          <Link
            href="/"
            className="group inline-flex h-10 items-center gap-2 rounded-full bg-white/10 pl-3 pr-4 text-[0.85rem] font-semibold text-white/90 ring-1 ring-white/15 transition-colors hover:bg-white/20 hover:text-white"
          >
            <ArrowLeft
              size={16}
              className="transition-transform group-hover:-translate-x-0.5"
            />
            Back to home
          </Link>
          <Link
            href="/"
            aria-label="Rankbist home"
            className="inline-flex h-10 items-center transition-transform hover:-translate-y-px"
          >
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/rankbist.png"
              alt="Rankbist"
              width={1781}
              height={375}
              className="h-[26px] w-auto sm:h-[32px]"
            />
          </Link>
        </header>

        <div className="grid flex-1 grid-cols-1 content-center gap-6 py-6 lg:grid-cols-[minmax(0,1fr)_440px] lg:items-center lg:gap-16">
          <AuthShowcase mode={mode} />

          <section className="mx-auto w-full max-w-[440px]">
            {/* The gradient edge is a 1px wrapper; nothing inside is clipped. */}
            <div className="rbl-card relative rounded-[26px] p-[1px] sm:rounded-[28px]">
              <div className="rba-card relative rounded-[25px] bg-white p-5 text-[#17142b] shadow-[0_40px_90px_-30px_rgba(0,0,0,0.65)] sm:rounded-[27px] sm:p-8">
                {children}
                {footer && (
                  <div className="rba-foot mt-5 border-t border-[#e1deef] pt-4 text-center">
                    <div className="text-[11.5px] leading-snug text-[#8d89a6]">
                      {footer}
                    </div>
                    <p className="mt-2 flex items-center justify-center gap-1.5 text-[12px] font-medium text-[#8d89a6]">
                      <ShieldCheck size={14} className="text-good" />
                      Your data is encrypted and never sold.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

/** "Already have an account? Log in" — lives inside the card, right under the button. */
export function AuthSwitch({ mode }: { mode: "login" | "signup" }) {
  const sw =
    mode === "login"
      ? {
          text: "New to Rankbist?",
          label: "Create an account",
          href: "/signup",
        }
      : { text: "Already have an account?", label: "Log in", href: "/login" };
  return (
    <p className="mt-4 text-center text-[0.88rem] text-[#5c5878]">
      {sw.text}{" "}
      <Link
        href={sw.href}
        className="font-extrabold text-[#4b33e0] underline-offset-4 hover:underline"
      >
        {sw.label}
      </Link>
    </p>
  );
}

/** Quiet "← Back" text button for the steps of a form. */
export function BackButton({
  onClick,
  children = "Back",
}: {
  onClick: () => void;
  children?: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="mt-3 inline-flex h-10 w-full items-center justify-center gap-1.5 rounded-2xl text-[0.86rem] font-bold text-[#5c5878] transition-colors hover:bg-[#f3f0ff] hover:text-[#4b33e0]"
    >
      <ArrowLeft size={15} /> {children}
    </button>
  );
}

/** Progress along a multi-step form: numbered dots joined by a line. */
export function StepBar({
  steps,
  current,
}: {
  steps: string[];
  current: number;
}) {
  return (
    <ol
      aria-label={`Step ${current + 1} of ${steps.length}`}
      className="mb-5 flex items-center sm:mb-6"
    >
      {steps.map((label, i) => {
        const done = i < current;
        const active = i === current;
        return (
          <li
            key={label}
            className={`flex items-center ${i < steps.length - 1 ? "flex-1" : ""}`}
            aria-current={active ? "step" : undefined}
          >
            <span className="flex items-center gap-2">
              <span
                className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[12px] font-extrabold transition-all duration-300 ${
                  done
                    ? "bg-good text-white"
                    : active
                      ? "bg-gradient-to-br from-[#8f78ff] to-[#4b33e0] text-white shadow-[0_8px_18px_-8px_rgba(106,82,255,0.9)]"
                      : "bg-[#efecfa] text-[#8d89a6]"
                }`}
              >
                {done ? <Check size={14} strokeWidth={3} /> : i + 1}
              </span>
              <span
                className={`text-[12.5px] font-bold ${active ? "text-[#17142b]" : "text-[#8d89a6]"} ${active ? "" : "hidden min-[400px]:inline"}`}
              >
                {label}
              </span>
            </span>
            {i < steps.length - 1 && (
              <span
                aria-hidden
                className={`mx-2.5 h-[2px] flex-1 rounded-full transition-colors duration-300 ${done ? "bg-good" : "bg-[#ece8f8]"}`}
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}

type Icon = ComponentType<{
  size?: number;
  className?: string;
  strokeWidth?: number;
}>;

export function AuthHeading({
  title,
  sub,
}: {
  /** Accepted for compatibility with older pages; the badge row was removed. */
  icon?: Icon;
  pill?: string;
  title: ReactNode;
  sub?: ReactNode;
}) {
  return (
    <div className="rba-hd mb-5 sm:mb-6">
      <h2 className="rba-h text-[1.3rem] font-extrabold leading-tight tracking-[-0.02em] sm:text-[1.65rem]">
        {title}
      </h2>
      {sub && (
        <p className="rba-sub mt-1.5 text-[13.5px] leading-relaxed text-[#5c5878]">
          {sub}
        </p>
      )}
    </div>
  );
}

export function OrDivider({ label = "or use email" }: { label?: string }) {
  return (
    <div className="rba-or my-4 flex items-center gap-3 text-[0.78rem] font-semibold text-[#8d89a6] sm:my-5">
      <div className="h-px flex-1 bg-[#e1deef]" />
      {label}
      <div className="h-px flex-1 bg-[#e1deef]" />
    </div>
  );
}

/* --------------------------------------------------------------------------
 * Fields: label above, rounded 14px box with an icon, like the admin form.
 * ------------------------------------------------------------------------ */

export function FieldLabel({
  htmlFor,
  children,
  aside,
}: {
  htmlFor: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="mb-1.5 flex items-center justify-between rba-label">
      <label
        htmlFor={htmlFor}
        className="text-[12.5px] font-bold text-[#17142b]"
      >
        {children}
      </label>
      {aside}
    </div>
  );
}

export function FieldError({
  id,
  children,
}: {
  id: string;
  children?: ReactNode;
}) {
  if (!children) return null;
  return (
    <p
      id={id}
      role="alert"
      className="auth-shake mt-1 flex items-center gap-1.5 text-[0.76rem] font-semibold text-warn"
    >
      <CircleAlert size={13} className="flex-shrink-0" />
      {children}
    </p>
  );
}

type FieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "className"> & {
  id: string;
  label: string;
  icon: Icon;
  error?: string | null;
  aside?: ReactNode;
};

export const Field = forwardRef<HTMLInputElement, FieldProps>(function Field(
  { id, label, icon: I, error, aside, ...rest },
  ref,
) {
  return (
    <div>
      <FieldLabel htmlFor={id} aside={aside}>
        {label}
      </FieldLabel>
      <div className="relative">
        <I
          size={16}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8d89a6]"
        />
        <input
          ref={ref}
          id={id}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          className={`rbl-input ${error ? "rbl-invalid" : ""}`}
          {...rest}
        />
      </div>
      <FieldError id={`${id}-err`}>{error}</FieldError>
    </div>
  );
});

/** Password with a show/hide eye and (optionally) a strength bar along the bottom edge. */
export function PasswordField({
  id,
  label = "Password",
  icon: I,
  value,
  onChange,
  error,
  aside,
  meter,
  autoComplete,
  placeholder,
  minLength,
}: {
  id: string;
  label?: string;
  icon: Icon;
  value: string;
  onChange: (v: string) => void;
  error?: string | null;
  aside?: ReactNode;
  meter?: boolean;
  autoComplete: string;
  placeholder: string;
  minLength?: number;
}) {
  const [show, setShow] = useState(false);
  const s = strength(value);
  const showMeter = !!meter && !!value && !error;
  return (
    <div>
      <FieldLabel
        htmlFor={id}
        aside={
          aside ??
          (showMeter ? (
            <span
              aria-live="polite"
              className="text-[12px] font-bold"
              style={{ color: s.color }}
            >
              {s.label}
            </span>
          ) : null)
        }
      >
        {label}
      </FieldLabel>
      <div className="relative">
        <I
          size={16}
          className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8d89a6]"
        />
        <input
          id={id}
          type={show ? "text" : "password"}
          required
          minLength={minLength}
          autoComplete={autoComplete}
          placeholder={placeholder}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-invalid={!!error || undefined}
          aria-describedby={error ? `${id}-err` : undefined}
          className={`rbl-input !pr-12 ${error ? "rbl-invalid" : ""}`}
        />
        <button
          type="button"
          onClick={() => setShow((v) => !v)}
          className="absolute right-1.5 top-1/2 flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-xl text-[#8d89a6] transition-colors hover:bg-[#ebe7ff] hover:text-[#4b33e0]"
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? <EyeOff size={16} /> : <Eye size={16} />}
        </button>
        {showMeter && (
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-3.5 bottom-[3px] flex gap-1"
          >
            {[0, 1, 2, 3].map((i) => (
              <span
                key={i}
                className="h-[3px] flex-1 rounded-full transition-colors duration-300"
                style={{ background: i < s.score ? s.color : "#ece8f8" }}
              />
            ))}
          </div>
        )}
      </div>
      <FieldError id={`${id}-err`}>{error}</FieldError>
    </div>
  );
}

function strength(pw: string): { score: number; label: string; color: string } {
  if (pw.length < 8) return { score: 1, label: "Too short", color: "#d6483f" };
  let n = 1;
  if (/[a-z]/.test(pw) && /[A-Z]/.test(pw)) n++;
  if (/\d/.test(pw)) n++;
  if (/[^A-Za-z0-9]/.test(pw) || pw.length >= 14) n++;
  return [
    { score: 1, label: "Weak", color: "#d6483f" },
    { score: 2, label: "Fair", color: "#e08a1e" },
    { score: 3, label: "Good", color: "#6b8afd" },
    { score: 4, label: "Strong", color: "#2f9e63" },
  ][n - 1];
}

/* --------------------------------------------------------------------------
 * Messages and the main button
 * ------------------------------------------------------------------------ */

export function Alert({
  tone,
  title,
  children,
  action,
}: {
  tone: "error" | "success" | "info";
  title?: ReactNode;
  children?: ReactNode;
  action?: ReactNode;
}) {
  const t = {
    error: {
      box: "border-warn/25 bg-warn-pale/70 text-[#8f2a23]",
      icon: <CircleAlert size={17} className="text-warn" />,
    },
    success: {
      box: "border-good/25 bg-good-pale/80 text-[#1d6a42]",
      icon: <CircleCheck size={17} className="text-good" />,
    },
    info: {
      box: "border-[#6a52ff]/20 bg-[#ebe7ff]/70 text-[#4b33e0]",
      icon: <CircleCheck size={17} className="text-[#6a52ff]" />,
    },
  }[tone];
  return (
    <div
      role={tone === "error" ? "alert" : "status"}
      className={`auth-pop flex gap-2.5 rounded-2xl border px-3.5 py-2.5 text-[0.82rem] leading-snug ${t.box}`}
    >
      <span className="mt-px flex-shrink-0">{t.icon}</span>
      <div className="min-w-0 flex-1">
        {title && <div className="font-extrabold">{title}</div>}
        {children && (
          <div
            className={
              title ? "mt-0.5 font-medium opacity-90" : "font-semibold"
            }
          >
            {children}
          </div>
        )}
        {action && <div className="mt-2">{action}</div>}
      </div>
    </div>
  );
}

export function SubmitButton({
  loading,
  disabled,
  children,
  loadingText,
}: {
  loading?: boolean;
  disabled?: boolean;
  children: ReactNode;
  loadingText: string;
}) {
  return (
    <button
      type="submit"
      disabled={loading || disabled}
      className="rbl-submit group relative flex h-12 w-full items-center justify-center gap-2 overflow-hidden rounded-2xl bg-gradient-to-br from-[#8f78ff] via-[#6a52ff] to-[#4b33e0] text-[14.5px] font-bold text-white shadow-[0_18px_36px_-14px_rgba(106,82,255,0.9),inset_0_1px_0_rgba(255,255,255,0.25)] transition-[transform,box-shadow,opacity] hover:-translate-y-0.5 disabled:translate-y-0 disabled:opacity-60"
    >
      <span
        aria-hidden
        className="auth-sheen pointer-events-none absolute inset-0"
      />
      {loading ? (
        <>
          <LoaderCircle size={17} className="animate-spin" /> {loadingText}
        </>
      ) : (
        <>
          {children}{" "}
          <ArrowRight
            size={16}
            className="transition-transform group-hover:translate-x-0.5"
          />
        </>
      )}
    </button>
  );
}

/* --------------------------------------------------------------------------
 * 6-digit code: one box per digit, paste anywhere, arrows and backspace move.
 * ------------------------------------------------------------------------ */

export function OtpInput({
  value,
  onChange,
  onComplete,
  invalid,
  disabled,
}: {
  value: string;
  onChange: (v: string) => void;
  onComplete?: (v: string) => void;
  invalid?: boolean;
  disabled?: boolean;
}) {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  useEffect(() => {
    refs.current[Math.min(value.length, 5)]?.focus();
    // first box on mount only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (next: string, focusAt: number) => {
    const clean = next.replace(/\D/g, "").slice(0, 6);
    onChange(clean);
    refs.current[Math.min(focusAt, 5)]?.focus();
    if (clean.length === 6) onComplete?.(clean);
  };

  const onKey = (i: number, e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      if (value[i]) set(value.slice(0, i) + value.slice(i + 1), i);
      else if (i > 0) set(value.slice(0, i - 1) + value.slice(i), i - 1);
    } else if (e.key === "ArrowLeft" && i > 0) refs.current[i - 1]?.focus();
    else if (e.key === "ArrowRight" && i < 5) refs.current[i + 1]?.focus();
  };

  const onPaste = (e: ClipboardEvent<HTMLInputElement>) => {
    const digits = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);
    if (!digits) return;
    e.preventDefault();
    set(digits, digits.length);
  };

  return (
    <div
      className={`flex justify-between gap-2 sm:gap-2.5 ${invalid ? "auth-shake" : ""}`}
      role="group"
      aria-label="6-digit code"
    >
      {Array.from({ length: 6 }, (_, i) => {
        const filled = !!value[i];
        return (
          <input
            key={i}
            ref={(el) => {
              refs.current[i] = el;
            }}
            value={value[i] ?? ""}
            disabled={disabled}
            inputMode="numeric"
            autoComplete={i === 0 ? "one-time-code" : "off"}
            aria-label={`Digit ${i + 1}`}
            maxLength={i === 0 ? 6 : 1}
            onPaste={onPaste}
            onKeyDown={(e) => onKey(i, e)}
            onFocus={(e) => e.currentTarget.select()}
            onChange={(e) => {
              const v = e.target.value.replace(/\D/g, "");
              if (!v) return;
              // a one-time-code autofill can drop all six digits into the first box
              if (v.length >= 6) return set(v, 6);
              const d = v.slice(-1);
              set(value.slice(0, i) + d + value.slice(i + 1), i + 1);
            }}
            className={`h-14 w-full min-w-0 rounded-[14px] border bg-white text-center text-[1.45rem] font-extrabold text-ink outline-none transition-[border-color,box-shadow,transform,background-color] duration-200 sm:h-[60px] ${
              invalid
                ? "border-warn/70 bg-warn-pale/40"
                : filled
                  ? "border-violet/60 bg-violet-pale/40"
                  : "border-[#e1deef]"
            } focus:-translate-y-0.5 focus:border-violet focus:shadow-[0_0_0_4px_rgba(143,103,255,0.16)]`}
          />
        );
      })}
    </div>
  );
}

/* -------------------------------------------------------------------------- */

const AUTH_CSS = `
.rb-auth .rbl-input { width: 100%; height: 48px; padding: 0 14px 0 40px; border-radius: 14px; border: 1px solid #e1deef; background: #fff; color: #17142b; font-size: 14.5px; outline: none; transition: border-color .15s, box-shadow .15s }
.rb-auth .rbl-input::placeholder { color: #a9a5c0 }
.rb-auth .rbl-input:hover { border-color: #c9c0f5 }
.rb-auth .rbl-input:focus { border-color: #6a52ff; box-shadow: 0 0 0 4px rgba(106,82,255,.14) }
.rb-auth .rbl-invalid, .rb-auth .rbl-invalid:hover, .rb-auth .rbl-invalid:focus { border-color: rgba(214,72,63,.7); box-shadow: 0 0 0 4px rgba(214,72,63,.10) }
/* phone field: the library's box restyled to match */
.rb-auth .PhoneInput { height: 48px; padding: 0 14px; gap: 8px; border: 1px solid #e1deef; border-radius: 14px; background: #fff; transition: border-color .15s, box-shadow .15s }
.rb-auth .PhoneInput:hover { border-color: #c9c0f5 }
.rb-auth .PhoneInput--focus { border-color: #6a52ff; box-shadow: 0 0 0 4px rgba(106,82,255,.14) }
.rb-auth .rb-phone-invalid .PhoneInput { border-color: rgba(214,72,63,.7); box-shadow: 0 0 0 4px rgba(214,72,63,.10) }
.rb-auth .PhoneInputInput { padding: 0; font-size: 14.5px; color: #17142b }
@media (max-width: 640px) { .rb-auth .rbl-input, .rb-auth .PhoneInputInput { font-size: 16px } .rb-auth .rbl-input, .rb-auth .PhoneInput { height: 50px } }
.rb-auth .rbl-step { animation: rbl-step .35s cubic-bezier(.2,.8,.2,1) both }
@keyframes rbl-step { from { opacity: 0; transform: translateX(14px) } to { opacity: 1; transform: none } }
.rb-auth .PhoneInput { height: 48px }
.rbl-grid { background-image: linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px); background-size: 44px 44px; -webkit-mask-image: radial-gradient(ellipse 80% 70% at 30% 40%, #000 30%, transparent 75%); mask-image: radial-gradient(ellipse 80% 70% at 30% 40%, #000 30%, transparent 75%) }
.rbl-card { background: linear-gradient(140deg, rgba(201,184,255,.9), rgba(255,155,213,.6) 45%, rgba(197,140,240,.7)) }
@keyframes rbl-glow { 0%,100% { transform: translate3d(0,0,0) } 50% { transform: translate3d(40px,30px,0) } }
@keyframes rbl-in { from { opacity: 0; transform: translateY(10px) } to { opacity: 1; transform: none } }
@keyframes rbl-feed { to { transform: translateY(-50%) } }
@keyframes rbl-dot { 0%,100% { box-shadow: 0 0 0 0 rgba(52,211,153,.55) } 70% { box-shadow: 0 0 0 6px rgba(52,211,153,0) } }
@keyframes auth-pop { from { opacity: 0; transform: translateY(-6px) scale(.98) } to { opacity: 1; transform: none } }
@keyframes auth-shake { 0%,100% { transform: none } 20% { transform: translateX(-5px) } 40% { transform: translateX(5px) } 60% { transform: translateX(-3px) } 80% { transform: translateX(3px) } }
@keyframes auth-sheen { from { transform: translateX(-120%) } to { transform: translateX(120%) } }
.rbl-glow { animation: rbl-glow 14s ease-in-out infinite }
.rbl-in { animation: rbl-in .55s cubic-bezier(.2,.8,.2,1) both }
.rbl-feed { animation: rbl-feed 18s linear infinite }
.rbl-feed-mask { -webkit-mask-image: linear-gradient(to bottom, transparent, #000 25%, #000 75%, transparent); mask-image: linear-gradient(to bottom, transparent, #000 25%, #000 75%, transparent) }
.rbl-dot { animation: rbl-dot 2s ease-out infinite }
.auth-pop { animation: auth-pop .32s cubic-bezier(.2,.8,.2,1) both }
.auth-shake { animation: auth-shake .4s ease both }
.auth-sheen { background: linear-gradient(110deg, transparent 30%, rgba(255,255,255,.25) 50%, transparent 70%); transform: translateX(-120%) }
.group:hover > .auth-sheen { animation: auth-sheen .8s ease }
@media (prefers-reduced-motion: reduce) { .rbl-glow, .rbl-step, .rbl-in, .rbl-feed, .rbl-dot, .auth-pop, .auth-shake, .group:hover > .auth-sheen { animation: none } }
`;
